import {Environment} from 'common/enums'
import {initializeApp} from 'firebase/app'
import {connectAuthEmulator, getAuth} from 'firebase/auth'
import {connectStorageEmulator, getStorage} from 'firebase/storage'
import {registerAppCheckTokenGetter} from 'lib/api/appCheck'
import {
  ERRORS_CODE_MESSAGES_MAP,
  createAuthService,
  createStorageService,
  createProductService,
  createDealService,
  createUserService,
  createAdminService,
  createNotificationService,
  createCategoryService,
  createChatService,
  createInvoiceService,
  createQueryService
} from './services'

export {ERRORS_CODE_MESSAGES_MAP}

const environment =
  (process.env.REACT_APP_PUBLIC_APPLICATION_ENVIRONMENT as Environment) ||
  Environment.DEVELOPMENT

const firebaseConfig = {
  [Environment.DEVELOPMENT]: {
    appId: process.env.REACT_APP_PUBLIC_DEVELOPMENT_APP_ID,
    apiKey: process.env.REACT_APP_PUBLIC_DEVELOPMENT_API_KEY,
    authDomain: process.env.REACT_APP_PUBLIC_DEVELOPMENT_AUTH_DOMAIN,
    databaseURL: process.env.REACT_APP_PUBLIC_DEVELOPMENT_DATABASE_URL,
    projectId: process.env.REACT_APP_PUBLIC_DEVELOPMENT_PROJECT_ID,
    storageBucket: process.env.REACT_APP_PUBLIC_DEVELOPMENT_STORAGE_BUCKET
  },
  [Environment.STAGING]: {
    appId: process.env.REACT_APP_PUBLIC_STAGING_APP_ID,
    apiKey: process.env.REACT_APP_PUBLIC_STAGING_API_KEY,
    authDomain: process.env.REACT_APP_PUBLIC_STAGING_AUTH_DOMAIN,
    databaseURL: process.env.REACT_APP_PUBLIC_STAGING_DATABASE_URL,
    projectId: process.env.REACT_APP_PUBLIC_STAGING_PROJECT_ID,
    storageBucket: process.env.REACT_APP_PUBLIC_STAGING_STORAGE_BUCKET
  },
  [Environment.PRODUCTION]: {
    appId: process.env.REACT_APP_PUBLIC_PRODUCTION_APP_ID,
    apiKey: process.env.REACT_APP_PUBLIC_PRODUCTION_API_KEY,
    authDomain: process.env.REACT_APP_PUBLIC_PRODUCTION_AUTH_DOMAIN,
    databaseURL: process.env.REACT_APP_PUBLIC_PRODUCTION_DATABASE_URL,
    projectId: process.env.REACT_APP_PUBLIC_PRODUCTION_PROJECT_ID,
    storageBucket: process.env.REACT_APP_PUBLIC_PRODUCTION_STORAGE_BUCKET
  }
}

class Firebase {
  private static instance: Firebase

  public app: ReturnType<typeof initializeApp>
  public auth: ReturnType<typeof getAuth>
  public firestore: null
  public database: null
  public storage: ReturnType<typeof getStorage>

  // Auth
  public sessionObserver!: ReturnType<
    typeof createAuthService
  >['sessionObserver']
  public signIn!: ReturnType<typeof createAuthService>['signIn']
  public resolveMfaSignIn!: ReturnType<
    typeof createAuthService
  >['resolveMfaSignIn']
  public reauthenticateAdmin!: ReturnType<
    typeof createAuthService
  >['reauthenticateAdmin']
  public completeAdminReauthentication!: ReturnType<
    typeof createAuthService
  >['completeAdminReauthentication']
  public getMfaStatus!: ReturnType<typeof createAuthService>['getMfaStatus']
  public beginTotpEnrollment!: ReturnType<
    typeof createAuthService
  >['beginTotpEnrollment']
  public completeTotpEnrollment!: ReturnType<
    typeof createAuthService
  >['completeTotpEnrollment']
  public signOut!: ReturnType<typeof createAuthService>['signOut']
  public resetPassword!: ReturnType<typeof createAuthService>['resetPassword']
  public changePassword!: ReturnType<typeof createAuthService>['changePassword']
  public checkCustomClaims!: ReturnType<
    typeof createAuthService
  >['checkCustomClaims']
  public getUserAccounts!: ReturnType<
    typeof createAuthService
  >['getUserAccounts']
  // Storage
  public getFileInfoFromStorage!: ReturnType<
    typeof createStorageService
  >['getFileInfoFromStorage']
  public uploadFile!: ReturnType<typeof createStorageService>['uploadFile']
  public deleteFile!: ReturnType<typeof createStorageService>['deleteFile']
  // Product
  public deleteProduct!: ReturnType<
    typeof createProductService
  >['deleteProduct']
  public getNewProductCode!: ReturnType<
    typeof createProductService
  >['getNewProductCode']
  public setProductDocument!: ReturnType<
    typeof createProductService
  >['setProductDocument']
  public updateProductDetails!: ReturnType<
    typeof createProductService
  >['updateProductDetails']
  public getProductViewCount!: ReturnType<
    typeof createProductService
  >['getProductViewCount']
  public getProductDocumentData!: ReturnType<
    typeof createProductService
  >['getProductDocumentData']
  public updateRequestedChanges!: ReturnType<
    typeof createProductService
  >['updateRequestedChanges']
  public getProductBids!: ReturnType<
    typeof createProductService
  >['getProductBids']
  // Deal
  public recallAgreement!: ReturnType<
    typeof createDealService
  >['recallAgreement']
  public signAgreement!: ReturnType<typeof createDealService>['signAgreement']
  public manualSignAgreement!: ReturnType<
    typeof createDealService
  >['manualSignAgreement']
  public updateDeal!: ReturnType<typeof createDealService>['updateDeal']
  public createDeal!: ReturnType<typeof createDealService>['createDeal']
  public getUserDeals!: ReturnType<typeof createDealService>['getUserDeals']
  // User (contacts & accounts)
  public createAccount!: ReturnType<typeof createUserService>['createAccount']
  public deleteAccount!: ReturnType<typeof createUserService>['deleteAccount']
  public editAccount!: ReturnType<typeof createUserService>['editAccount']
  public createContact!: ReturnType<typeof createUserService>['createContact']
  public deleteContact!: ReturnType<typeof createUserService>['deleteContact']
  public editContact!: ReturnType<typeof createUserService>['editContact']
  public updateContactStatus!: ReturnType<
    typeof createUserService
  >['updateContactStatus']
  public getPrimaryContact!: ReturnType<
    typeof createUserService
  >['getPrimaryContact']
  // Admin
  public createAdmin!: ReturnType<typeof createAdminService>['createAdmin']
  public editAdmin!: ReturnType<typeof createAdminService>['editAdmin']
  public deleteAdmin!: ReturnType<typeof createAdminService>['deleteAdmin']
  public grantDenyAdminAccess!: ReturnType<
    typeof createAdminService
  >['grantDenyAdminAccess']
  public waiveBidderDeposit!: ReturnType<
    typeof createAdminService
  >['waiveBidderDeposit']
  public getUserPersonalDetail!: ReturnType<
    typeof createAdminService
  >['getUserPersonalDetail']
  public updateUserPersonalDetail!: ReturnType<
    typeof createAdminService
  >['updateUserPersonalDetail']
  public getUserBankAccountInfo!: ReturnType<
    typeof createAdminService
  >['getUserBankAccountInfo']
  public updateAdminBankAccountInfo!: ReturnType<
    typeof createAdminService
  >['updateAdminBankAccountInfo']
  public getAdminDocument!: ReturnType<
    typeof createAdminService
  >['getAdminDocument']
  public updateFinanceStatus!: ReturnType<
    typeof createAdminService
  >['updateFinanceStatus']
  public updateExemptionTypeAndRegions!: ReturnType<
    typeof createAdminService
  >['updateExemptionTypeAndRegions']
  public updateExemptionCertificate!: ReturnType<
    typeof createAdminService
  >['updateExemptionCertificate']
  // Notification
  public updateLastViewedNotification!: ReturnType<
    typeof createNotificationService
  >['updateLastViewedNotification']
  public markAllNotiicationsAsRead!: ReturnType<
    typeof createNotificationService
  >['markAllNotiicationsAsRead']
  public markNotificationAsRead!: ReturnType<
    typeof createNotificationService
  >['markNotificationAsRead']
  public getNotifications!: ReturnType<
    typeof createNotificationService
  >['getNotifications']
  public getNextNotifications!: ReturnType<
    typeof createNotificationService
  >['getNextNotifications']
  public clearPushSubscription!: ReturnType<
    typeof createNotificationService
  >['clearPushSubscription']
  public requestNotifactionsPermission!: ReturnType<
    typeof createNotificationService
  >['requestNotifactionsPermission']
  // Category
  public getSectors!: ReturnType<typeof createCategoryService>['getSectors']
  public getCategories!: ReturnType<
    typeof createCategoryService
  >['getCategories']
  public getSingleCategory!: ReturnType<
    typeof createCategoryService
  >['getSingleCategory']
  public getSingleSector!: ReturnType<
    typeof createCategoryService
  >['getSingleSector']
  public createOrEditSector!: ReturnType<
    typeof createCategoryService
  >['createOrEditSector']
  public createOrEditCateogry!: ReturnType<
    typeof createCategoryService
  >['createOrEditCateogry']
  public createOrEditSubcateogry!: ReturnType<
    typeof createCategoryService
  >['createOrEditSubcateogry']
  public deleteCateogry!: ReturnType<
    typeof createCategoryService
  >['deleteCateogry']
  public deleteSector!: ReturnType<typeof createCategoryService>['deleteSector']
  public deleteSubcateogry!: ReturnType<
    typeof createCategoryService
  >['deleteSubcateogry']
  public getCategoriesList!: ReturnType<
    typeof createCategoryService
  >['getCategoriesList']
  public getSectorsList!: ReturnType<
    typeof createCategoryService
  >['getSectorsList']
  // Chat
  public getUserChat!: ReturnType<typeof createChatService>['getUserChat']
  public sendChatMessage!: ReturnType<
    typeof createChatService
  >['sendChatMessage']
  public archiveUserChat!: ReturnType<
    typeof createChatService
  >['archiveUserChat']
  public unarchiveUserChat!: ReturnType<
    typeof createChatService
  >['unarchiveUserChat']
  public deleteUserChat!: ReturnType<typeof createChatService>['deleteUserChat']
  public deleteChatMessage!: ReturnType<
    typeof createChatService
  >['deleteChatMessage']
  public markChatAsRead!: ReturnType<typeof createChatService>['markChatAsRead']
  public restoreUserChat!: ReturnType<
    typeof createChatService
  >['restoreUserChat']
  // Invoice
  public sendNoticeOfRelease!: ReturnType<
    typeof createInvoiceService
  >['sendNoticeOfRelease']
  public addLineItem!: ReturnType<typeof createInvoiceService>['addLineItem']
  public removeInvoiceItem!: ReturnType<
    typeof createInvoiceService
  >['removeInvoiceItem']
  public markBuyerWireAsPaid!: ReturnType<
    typeof createInvoiceService
  >['markBuyerWireAsPaid']
  public addInvoiceComment!: ReturnType<
    typeof createInvoiceService
  >['addInvoiceComment']
  // Query & Sync
  public handleAPIRequest!: ReturnType<
    typeof createQueryService
  >['handleAPIRequest']
  public handleSyncFirebaseToZohoAccountAPI!: ReturnType<
    typeof createQueryService
  >['handleSyncFirebaseToZohoAccountAPI']
  public handleSyncZohoToFirebaseProductAPI!: ReturnType<
    typeof createQueryService
  >['handleSyncZohoToFirebaseProductAPI']
  public handleSyncZohoToFirebaseAccountAPI!: ReturnType<
    typeof createQueryService
  >['handleSyncZohoToFirebaseAccountAPI']
  public handleSyncFirebaseToZohoProductAPI!: ReturnType<
    typeof createQueryService
  >['handleSyncFirebaseToZohoProductAPI']
  public handleSyncZohoToFirebaseWebsiteAccountAPI!: ReturnType<
    typeof createQueryService
  >['handleSyncZohoToFirebaseWebsiteAccountAPI']
  public getCollectionBasedOnQuery!: ReturnType<
    typeof createQueryService
  >['getCollectionBasedOnQuery']
  public getCollectionSnapshotBasedOnQuery!: ReturnType<
    typeof createQueryService
  >['getCollectionSnapshotBasedOnQuery']
  public getCollectionBasedOnIds!: ReturnType<
    typeof createQueryService
  >['getCollectionBasedOnIds']
  public getCollectionData!: ReturnType<
    typeof createQueryService
  >['getCollectionData']
  public checkDocuemnt!: ReturnType<typeof createQueryService>['checkDocuemnt']
  public getDocumentData!: ReturnType<
    typeof createQueryService
  >['getDocumentData']
  public getSingleDocumentData!: ReturnType<
    typeof createQueryService
  >['getSingleDocumentData']

  private constructor() {
    const useEmulators = process.env.REACT_APP_USE_EMULATOR === 'true'

    this.app = initializeApp(firebaseConfig[environment])
    this.auth = getAuth()
    this.firestore = null
    this.database = null
    this.storage = getStorage()

    if (typeof window !== 'undefined') {
      ;(
        window as Window & {
          __DADE_GET_ID_TOKEN__?: () => Promise<string | null>
        }
      ).__DADE_GET_ID_TOKEN__ = async () =>
        this.auth.currentUser ? this.auth.currentUser.getIdToken() : null
      registerAppCheckTokenGetter()
    }

    if (useEmulators) {
      console.log('Firebase is running in dev mode...')
      connectAuthEmulator(this.auth, 'http://localhost:9099')
      connectStorageEmulator(this.storage, 'localhost', 9199)
    } else {
      console.log('Firebase is running in production mode!')
    }

    const fb = {
      app: this.app,
      auth: this.auth,
      firestore: this.firestore,
      database: this.database,
      storage: this.storage
    }

    // Wire up all service delegates
    Object.assign(this, createAuthService(fb))
    Object.assign(this, createStorageService(fb))
    Object.assign(this, createProductService(fb))
    Object.assign(this, createDealService(fb))
    Object.assign(this, createUserService(fb))
    Object.assign(this, createAdminService(fb))
    Object.assign(this, createNotificationService(fb))
    Object.assign(this, createCategoryService(fb))
    Object.assign(this, createChatService(fb))
    Object.assign(this, createInvoiceService(fb))
    Object.assign(this, createQueryService(fb))
  }

  public static getInstance(): Firebase {
    if (!Firebase.instance) {
      Firebase.instance = new Firebase()
    }
    return Firebase.instance
  }
}

const firebase = Firebase.getInstance()

export default firebase
